import type {
    DailyOperationStatus,
    DailyOperationType,
    DailyQueueDayFilter,
    DailyQueuePageDto,
    DailyQueueRowDto,
    OperationClass,
    ReasonCode,
    StageApplicability,
    StageKey,
} from "@/features/daily/types/daily.types";
import type { DailyQueryState } from "@/features/daily/hooks/use-daily-query-state";
import type { DailyError } from "@/features/daily/hooks/use-daily-resource";
import {
    DailyTone,
    commandViewModel,
    dailyFormat,
} from "@/features/daily/components/command/command-view-model";
import { dailyDates } from "@/features/daily/components/daily-dates";

/** The queue vocabulary is the backend's, read off the frozen wire contract. */
export type DailyQueueKind = DailyQueuePageDto["queue"];

/**
 * FR-018: the operational queues are distinct questions, each with its own
 * population. `day` (B3) is the operations created on one Madrid calendar day and is
 * where the screen lands; `credit_stalled` is the population a `credit_stalled`
 * incident links to (D89). `search` is not a tab — it is the queue the backend
 * answers with when the operator's term is applied, so it can only ever arrive in a
 * response, never be picked from the navigation.
 */
export const DAILY_QUEUE_TABS = ["priority", "day", "overdue", "failures", "review", "history", "credit_stalled", "credit_backlog", "historic_stuck"] as const;
export type DailyQueueTab = (typeof DAILY_QUEUE_TABS)[number];

export const DAILY_DAY_FILTERS: readonly DailyQueueDayFilter[] = ["all", "problems", "cancelled", "in_progress", "overdue"];

/** D107: the three tabs of the day; «En curso» and «Fuera de plazo» are chips inside «Hoy». */
export const DAILY_DAY_TABS: readonly DailyQueueDayFilter[] = ["all", "problems", "cancelled"];
export const DAILY_DAY_CHIPS: readonly DailyQueueDayFilter[] = ["in_progress", "overdue"];

const DAY_FILTER_ICON: Record<DailyQueueDayFilter, string> = {
    all: "calendar_today",
    problems: "warning",
    cancelled: "cancel",
    in_progress: "pending",
    overdue: "schedule",
};

// L44: TOTAL over the closed vocabulary — tsc breaks the build if the backend adds
// a queue, instead of the UI silently rendering a blank tab.
const QUEUE_TEXT: Record<DailyQueueKind, string> = {
    priority: "Prioritarias",
    day: "Operaciones del día",
    overdue: "Vencidas",
    failures: "Fallos",
    review: "En revisión",
    history: "Historial",
    credit_stalled: "Créditos detenidos",
    search: "Resultados de la búsqueda",
    historic_stuck: "Atascadas históricas",
    credit_backlog: "Pendientes de regularizar",
};

const QUEUE_DESCRIPTION: Record<DailyQueueKind, string> = {
    priority: "Lo que conviene mirar primero: fuera de plazo, créditos detenidos y revisiones con fondos, en vivo y por urgencia.",
    day: "Operaciones creadas ese día, en hora de Madrid.",
    overdue: "Operaciones financiadas que superaron el plazo de su etapa, más todo lo retenido por un bloqueo acreditado.",
    failures: "Operaciones que terminaron fallidas, canceladas o reembolsadas.",
    review: "Operaciones retenidas para revisión manual.",
    history: "Operaciones ya completadas en esta ventana.",
    credit_stalled: "Depósitos cuyos fondos se reclamaron pero nunca se abonaron, pasada la antigüedad mínima.",
    search: "Operaciones que coinciden con lo que buscaste.",
    historic_stuck: "Operaciones fuera de plazo desde hace más de 30 días; no cuentan para el estado en vivo.",
    credit_backlog: "Depósitos con fondos recibidos y sin acreditar desde hace entre 7 y 30 días.",
};

const DAY_FILTER_TEXT: Record<DailyQueueDayFilter, string> = {
    all: "Hoy",
    problems: "Con problemas",
    cancelled: "Canceladas",
    in_progress: "En curso",
    overdue: "Fuera de plazo",
};

/** D119: what each tab holds; «Hoy» leaves the cancelled ones to their own tab. */
const DAY_FILTER_DESCRIPTION: Record<DailyQueueDayFilter, string> = {
    all: "Operaciones creadas, con dinero recibido o completadas hoy (sin canceladas)",
    problems: "Operaciones del día fallidas, reembolsadas o con problemas",
    cancelled: "Solo las operaciones del día canceladas",
    in_progress: "Financiadas y todavía abiertas",
    overdue: "Abiertas que superaron el plazo de su etapa",
};

const DAY_FILTER_TITLE: Record<DailyQueueDayFilter, string> = {
    all: "Operaciones del día",
    problems: "Operaciones con problemas",
    cancelled: "Operaciones canceladas",
    in_progress: "Operaciones en curso",
    overdue: "Operaciones fuera de plazo",
};

const OPERATION_TYPE_TEXT: Record<DailyOperationType, string> = {
    order: "Orden",
    deposit: "Depósito",
    trade: "Trade",
    withdrawal: "Retiro",
};

const OPERATION_STATUS_TEXT: Record<DailyOperationStatus, string> = {
    Pending: "Pendiente",
    Received: "Recibida",
    Processing: "En curso",
    Reviewing: "En revisión",
    Success: "Completada",
    Failed: "Fallida",
    Canceled: "Cancelada",
    Refunded: "Reembolsada",
    Deleted: "Eliminada",
};

// FR-002: `total − completed` is not a class. Every row carries the class the
// backend assigned it, `unknown` included and visible.
const OPERATION_CLASS_TEXT: Record<OperationClass, string> = {
    awaiting_funds: "Esperando fondos",
    funded_in_progress: "Financiada, en curso",
    in_review: "En revisión",
    completed: "Completada",
    failed: "Fallida",
    canceled: "Cancelada",
    refunded: "Reembolsada",
    unknown: "Sin clasificar",
};

/**
 * D107: the status pill's colour — green done, amber waiting or moving, blue in
 * review, red failed, grey closed without completing. The word is always there.
 */
const OPERATION_STATUS_TONE: Record<DailyOperationStatus, "good" | DailyTone> = {
    Pending: "warning",
    Received: "warning",
    Processing: "warning",
    Reviewing: "info",
    Success: "good",
    Failed: "critical",
    Canceled: "neutral",
    Refunded: "neutral",
    Deleted: "neutral",
};

const createdDayFormatter = new Intl.DateTimeFormat("es-ES", { timeZone: "Europe/Madrid", day: "numeric", month: "short" });

const createdFormatter = new Intl.DateTimeFormat("es-ES", {
    timeZone: "Europe/Madrid",
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
});

const pairOf = (pair: DailyQueueRowDto["pair"]): string =>
    pair.origin === null && pair.destination === null
        ? dailyFormat.absent
        : `${pair.origin ?? dailyFormat.absent}/${pair.destination ?? dailyFormat.absent}`;

const moneyOrAbsent = (amount: number | null, currency: string | null): string =>
    amount === null ? dailyFormat.absent : dailyFormat.money(amount, currency);

const OPEN_DURATION = "–";

const OPERATION_CLASS_TONE: Record<OperationClass, DailyTone> = {
    awaiting_funds: "neutral",
    funded_in_progress: "neutral",
    in_review: "warning",
    completed: "neutral",
    failed: "critical",
    canceled: "info",
    refunded: "info",
    unknown: "info",
};

const STAGE_TEXT: Record<StageKey, string> = {
    funding_wait: "Espera de fondos",
    exchange: "Conversión",
    outflow: "Entrega",
    total_funded: "Ejecución completa",
};

/**
 * Enmienda C6 / ruling D33: a row whose route could not be established is LABELLED,
 * never dropped and never quietly treated as "no exchange needed". The frozen queue
 * row carries no `stageApplicability` field, so the applicability arrives the only
 * way the contract offers it — as a reason code.
 */
const STAGE_APPLICABILITY_TEXT: Record<StageApplicability, string | null> = {
    applicable: null,
    not_applicable: "La etapa no aplica a esta ruta",
    unknown: "Etapa sin determinar",
};

export type QueueRowVm = {
    operationId: string;
    /** D107: creation, Madrid wall clock («22 sept 2026, 23:59»). */
    createdLabel: string;
    /**
     * D140: a row of the day created on an earlier day (money arrived or completed today) —
     * «creada el 23 sept», with the full timestamp as its title; `null` otherwise.
     */
    createdBeforeLabel: string | null;
    createdFullLabel: string;
    pairLabel: string;
    receivedLabel: string;
    projectedLabel: string;
    /** Duration once finished; «–» while the operation is still open. */
    durationLabel: string;
    statusTone: "good" | DailyTone;
    accountEmail: string | null;
    typeLabel: string;
    statusLabel: string;
    classLabel: string;
    classTone: DailyTone;
    stageLabel: string;
    applicabilityNote: string | null;
    stuck: boolean;
    /** FR-025: the state is carried by words. Colour only repeats what this says. */
    stateLabel: string;
    stateTone: DailyTone;
    ageMs: number;
    ageLabel: string;
    deadlineAt: number | null;
    deadlineLabel: string;
    pastDeadline: boolean;
    amount: { value: number; currency: string | null } | null;
    amountLabel: string;
    scopeLabel: string;
    reasons: string[];
    /**
     * D32: presentation only. The page order is the server's (oldest first); this
     * rank re-orders the PAGE ALREADY RECEIVED and never re-paginates.
     */
    impactRank: number;
};

export type QueueContextVm = {
    queueLabel: string;
    queueDescription: string;
    tableTitle: string;
    scopeLabel: string;
    windowLabel: string;
    timeZone: string;
    qualityLabel: string;
    tone: DailyTone;
    reasons: string[];
    policyLabel: string;
};

export type DayFilterVm = {
    filter: DailyQueueDayFilter;
    label: string;
    icon: string;
    description: string;
    href: string;
    current: boolean;
};

export type DayNavigatorVm = {
    date: string;
    /** The server's own "today" in Madrid, read off `evaluatedAt` — never the browser clock. */
    today: string;
    /** D105: the first and last day of the page's range; `min` is null with no range. */
    min: string | null;
    max: string;
    dateLabel: string;
    completenessLabel: string;
    previousHref: string | null;
    nextHref: string | null;
    todayHref: string | null;
};

/**
 * `server` keeps the server's order — newest first for the day and an email's history
 * (D138), oldest first for the problem queues. `oldest` re-sorts a newest-first page.
 */
export type QueueSortKey = "server" | "oldest" | "impact";

/** D99: the server refuses a malformed term with this token; the box turns it into words. */
export const INVALID_SEARCH_TERM = "invalid_search_term";

const INVALID_SEARCH_COPY = "Escribe un id de operación o un correo electrónico completo, sin espacios.";

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const isNumberOrNull = (value: unknown): boolean => value === null || typeof value === "number";

/** D24: the shared cache stores `unknown`; the caller owns the guard so nothing is cast. */
export const isQueuePageDto = (value: unknown): value is DailyQueuePageDto =>
    isRecord(value)
    && isRecord(value.envelope)
    && typeof value.queue === "string"
    && Array.isArray(value.rows)
    && isNumberOrNull(value.totalItems)
    && typeof value.truncated === "boolean"
    && (value.nextCursor === null || typeof value.nextCursor === "string")
    && Array.isArray(value.impactByCurrency);

const rowApplicability = (reasonCodes: ReasonCode[]): StageApplicability => {
    if (reasonCodes.includes("stage_applicability_unknown")) return "unknown";
    if (reasonCodes.includes("stage_not_applicable")) return "not_applicable";
    return "applicable";
};

const amountOf = (row: DailyQueueRowDto): { value: number; currency: string | null } | null =>
    row.amount === null ? null : { value: row.amount, currency: row.currency };

const money = (amount: { value: number; currency: string | null } | null): string =>
    amount === null ? dailyFormat.absent : dailyFormat.money(amount.value, amount.currency);

/**
 * "Past its deadline" is judged against the instant the SERVER evaluated the page,
 * never against this browser's clock: a tab left open would otherwise start calling
 * rows overdue on evidence that says nothing of the sort (ledger L354).
 */
const isPastDeadline = (deadlineAt: number | null, evaluatedAt: number): boolean =>
    deadlineAt !== null && deadlineAt <= evaluatedAt;

const stateLabelOf = (row: DailyQueueRowDto, pastDeadline: boolean): string => {
    if (row.stuck && pastDeadline) return "Detenida · fuera de plazo";
    if (row.stuck) return "Detenida";
    if (pastDeadline) return "Fuera de plazo";
    return "Dentro de plazo";
};

const stateToneOf = (row: DailyQueueRowDto, pastDeadline: boolean): DailyTone => {
    const classTone = OPERATION_CLASS_TONE[row.operationClass];
    if (classTone === "critical") return "critical";
    if (row.stuck || pastDeadline) return "warning";
    return classTone;
};

const readQueueOf = (raw: string | null): DailyQueueTab => {
    const match = DAILY_QUEUE_TABS.find((tab) => tab === raw);
    return match ?? "day";
};

const readDayFilterOf = (raw: string | null): DailyQueueDayFilter => {
    const match = DAILY_DAY_FILTERS.find((filter) => filter === raw);
    return match ?? "all";
};

const scopeParamsOf = (query: DailyQueryState): Record<string, string> => ({
    scope: query.scope.join(","),
    types: query.types.join(","),
});

const queueParamsOf = (query: DailyQueryState, queue: DailyQueueTab): Readonly<Record<string, string>> => {
    const paging = {
        cursor: query.cursor ?? "",
        page: query.page <= 1 ? "" : String(query.page),
    };
    // B3: the day IS the window. A window, bounds or a stage beside it would be a
    // second, contradictory question — the server answers 400, so none is sent.
    // D106: the day is read whole (every page, followed by the API), so no cursor or
    // page number of the URL belongs to its request.
    if (queue === "day") {
        return {
            queue,
            date: dailyDates.isIsoDate(query.date) ? query.date : "",
            filter: query.filter === null ? "" : readDayFilterOf(query.filter),
            ...scopeParamsOf(query),
        };
    }
    const custom = query.windowPreset === "custom";
    return {
        queue,
        window: query.windowPreset ?? "today",
        from: custom ? query.windowFrom ?? "" : "",
        to: custom ? query.windowTo ?? "" : "",
        ...scopeParamsOf(query),
        stage: query.stage ?? "",
        ...paging,
    };
};

// D105: the page's range lives in the URL beside the day queue so the header bar keeps
// showing it and the navigator stays inside it. `queueParamsOf` never SENDS it for
// `day` (the server refuses a window beside a day).
const rangeParamsOf = (query: DailyQueryState): Record<string, string> => ({
    window: query.windowPreset ?? "",
    from: query.windowFrom ?? "",
    to: query.windowTo ?? "",
});

/**
 * The days the navigator may visit: the header range when one is set, never past the
 * server's today. "Month to date" starts on the 1st of the server's month.
 */
const rangeOf = (query: DailyQueryState, today: string): { min: string | null; max: string } => {
    if (query.windowPreset === "month_to_date") return { min: `${today.slice(0, 8)}01`, max: today };
    const from = query.windowFrom === null ? Number.NaN : Number(query.windowFrom);
    const to = query.windowTo === null ? Number.NaN : Number(query.windowTo);
    if (query.windowPreset === "custom" && Number.isFinite(from) && Number.isFinite(to)) {
        const last = dailyDates.madridDateOf(to);
        return { min: dailyDates.madridDateOf(from), max: last < today ? last : today };
    }
    return { min: null, max: today };
};

const dayHref = (query: DailyQueryState, patch: { date?: string; filter?: DailyQueueDayFilter }): string =>
    commandViewModel.diagnosticHref({
        queue: "day",
        ...scopeParamsOf(query),
        ...rangeParamsOf(query),
        date: patch.date ?? (dailyDates.isIsoDate(query.date) ? query.date : ""),
        filter: patch.filter === undefined || patch.filter === "all" ? "" : patch.filter,
        incidentId: query.incidentId ?? "",
    });

// CH-8: FNV-1a, 32 bits. It only has to tell two terms apart inside an in-memory
// cache key; it is not a secret, and the term itself never reaches a key or a URL.
const searchDigestOf = (term: string): string => {
    let hash = 0x811c9dc5;
    for (let index = 0; index < term.length; index += 1) {
        hash ^= term.charCodeAt(index);
        hash = Math.imul(hash, 0x01000193) >>> 0;
    }
    return hash.toString(16);
};

export const diagnosticViewModel = {
    searchDigest: searchDigestOf,

    queueTabs: DAILY_QUEUE_TABS,

    queueLabel: (kind: DailyQueueKind): string => QUEUE_TEXT[kind],

    /** An unknown or absent `queue` parameter lands on the day view (the design's default). */
    readQueue: readQueueOf,

    readDayFilter: readDayFilterOf,

    /** D99: a term with `@` is an account email; the server answers with its history. */
    isEmailTerm: (term: string): boolean => term.includes("@"),

    /**
     * Enmienda C14: ONE builder feeds the table and the export, so an export can
     * never answer a different question from the one on screen.
     */
    queueParams: queueParamsOf,

    /**
     * FR-017: moving between queues keeps the scope, the window and the incident the
     * operator arrived with. The position inside the old queue and its `stage` filter are
     * dropped (D94): a cursor minted for one query is meaningless in another, and only
     * the overdue queue has stages. The day queue carries no window at all (B3).
     */
    queueTabHref: (query: DailyQueryState, queue: DailyQueueTab): string =>
        queue === "day"
            ? dayHref(query, {})
            : commandViewModel.diagnosticHref({
                queue,
                window: query.windowPreset ?? "today",
                from: query.windowFrom ?? "",
                to: query.windowTo ?? "",
                ...scopeParamsOf(query),
                // D94: a stage narrows only the overdue question; carried into another queue
                // it turns the tab into a 400 `stage_requires_overdue`.
                stage: queue === readQueueOf(query.queue) ? query.stage ?? "" : "",
                incidentId: query.incidentId ?? "",
            }),

    /** B3: the three filters of a day, each with the count the server stated for the WHOLE day. */
    dayFilters: (
        query: DailyQueryState,
        dto: DailyQueuePageDto | null,
        which: readonly DailyQueueDayFilter[] = DAILY_DAY_FILTERS,
    ): DayFilterVm[] => {
        const current = readDayFilterOf(query.filter);
        return which.map((filter) => {
            const count = dto === null || dto.counts === null ? null : dto.counts[filter];
            return {
                filter,
                label: count === null ? DAY_FILTER_TEXT[filter] : `${DAY_FILTER_TEXT[filter]} · ${dailyFormat.count(count)}`,
                icon: DAY_FILTER_ICON[filter],
                description: DAY_FILTER_DESCRIPTION[filter],
                href: dayHref(query, { filter }),
                current: filter === current,
            };
        });
    },

    /**
     * The navigator moves one Madrid day at a time and never past the server's today:
     * a future day has not happened, so there is nothing to show for it (and the
     * server refuses it with `day_in_future`).
     */
    dayNavigator: (query: DailyQueryState, dto: DailyQueuePageDto): DayNavigatorVm => {
        const today = dailyDates.madridDateOf(dto.envelope.evaluatedAt);
        const date = dto.date ?? (dailyDates.isIsoDate(query.date) ? query.date : today);
        const filter = readDayFilterOf(query.filter);
        const range = rangeOf(query, today);
        const isToday = date >= today;
        const atEnd = date >= range.max;
        return {
            date,
            today,
            min: range.min,
            max: range.max,
            dateLabel: dailyDates.dayLabel(date),
            completenessLabel: isToday
                ? `día en curso, corte ${dailyFormat.clock(dto.envelope.evaluatedAt)}`
                : "día completo",
            previousHref: range.min !== null && date <= range.min
                ? null
                : dayHref(query, { date: dailyDates.shift(date, -1), filter }),
            nextHref: atEnd ? null : dayHref(query, { date: dailyDates.shift(date, 1), filter }),
            todayHref: isToday || range.max < today ? null : dayHref(query, { date: today, filter }),
        };
    },

    dayHref: (query: DailyQueryState, date: string): string =>
        dayHref(query, { date, filter: readDayFilterOf(query.filter) }),

    /**
     * SC-018: the context of the page is read from the envelope alone. An empty
     * queue is an answer about the QUERY, not evidence about the platform, so it
     * can never upgrade a partial or stale reading into a clean one.
     */
    context: (dto: DailyQueuePageDto): QueueContextVm => ({
        queueLabel: QUEUE_TEXT[dto.queue],
        queueDescription: QUEUE_DESCRIPTION[dto.queue],
        tableTitle: dto.queue === "day" && dto.dayFilter !== null
            ? DAY_FILTER_TITLE[dto.dayFilter]
            : dto.queue === "search" ? "Resultados de la búsqueda"
                : dto.queue === "priority" ? "Operaciones prioritarias" : QUEUE_TEXT[dto.queue],
        scopeLabel: dto.envelope.scope.label,
        windowLabel: commandViewModel.windowLabel(dto.envelope.window),
        timeZone: dto.envelope.window.timeZone,
        qualityLabel: commandViewModel.qualityLabel(dto.envelope.quality),
        tone: commandViewModel.metricTone(dto.envelope.quality),
        reasons: commandViewModel.reasonTexts(dto.envelope.reasonCodes),
        policyLabel: dailyFormat.policy(dto.envelope.policy.policyVersion, dto.envelope.policy.policyFingerprint),
    }),

    rows: (dto: DailyQueuePageDto): QueueRowVm[] =>
        dto.rows.map((row) => diagnosticViewModel.row(row, dto.envelope.evaluatedAt)),

    /** One queue row judged against the instant the server evaluated it (D134 reuses it on Salud). */
    row: (row: DailyQueueRowDto, evaluatedAt: number): QueueRowVm => {
        const pastDeadline = isPastDeadline(row.deadlineAt, evaluatedAt);
        const amount = amountOf(row);
        const applicability = rowApplicability(row.reasonCodes);
        return {
            operationId: row.operationId,
            createdLabel: createdFormatter.format(new Date(row.createdAt)),
            createdBeforeLabel: row.createdBeforeDay ? `creada el ${createdDayFormatter.format(new Date(row.createdAt))}` : null,
            createdFullLabel: dailyFormat.stamp(row.createdAt),
            pairLabel: pairOf(row.pair),
            receivedLabel: moneyOrAbsent(row.receivedAmount, row.receivedCurrency),
            projectedLabel: moneyOrAbsent(row.projectedAmount, row.projectedCurrency),
            durationLabel: row.durationMs === null ? OPEN_DURATION : dailyFormat.duration(row.durationMs),
            statusTone: OPERATION_STATUS_TONE[row.status],
            // A value without «@» is an internal account id: no email link, no copy, never shown raw.
            accountEmail: row.accountEmail !== null && row.accountEmail.includes("@") ? row.accountEmail : null,
            typeLabel: OPERATION_TYPE_TEXT[row.operationType],
            statusLabel: OPERATION_STATUS_TEXT[row.status],
            classLabel: OPERATION_CLASS_TEXT[row.operationClass],
            classTone: OPERATION_CLASS_TONE[row.operationClass],
            stageLabel: row.stage === null ? dailyFormat.absent : STAGE_TEXT[row.stage],
            applicabilityNote: STAGE_APPLICABILITY_TEXT[applicability],
            stuck: row.stuck,
            stateLabel: stateLabelOf(row, pastDeadline),
            stateTone: stateToneOf(row, pastDeadline),
            ageMs: row.ageMs,
            ageLabel: dailyFormat.duration(row.ageMs),
            deadlineAt: row.deadlineAt,
            deadlineLabel: dailyFormat.stamp(row.deadlineAt),
            pastDeadline,
            amount,
            amountLabel: money(amount),
            scopeLabel: row.scope.label,
            reasons: commandViewModel.reasonTexts(row.reasonCodes),
            impactRank: (row.stuck ? 2 : 0) + (pastDeadline ? 1 : 0),
        };
    },

    /**
     * D32: `age` keeps the order the server paginated by (oldest first) untouched.
     * `impact` re-orders THIS page only — never a different page, never a different
     * population. Amounts are deliberately not a sort key: Daily has no FX, so
     * ranking 100 EUR against 90 000 VES would be a fiction.
     */
    sortRows: (rows: QueueRowVm[], sortKey: QueueSortKey): QueueRowVm[] => {
        if (sortKey === "server") return rows;
        if (sortKey === "oldest") {
            return rows
                .map((row, index) => ({ row, index }))
                .sort((left, right) => (left.row.ageMs !== right.row.ageMs ? right.row.ageMs - left.row.ageMs : left.index - right.index))
                .map((entry) => entry.row);
        }
        return rows
            .map((row, index) => ({ row, index }))
            .sort((left, right) => {
                if (left.row.impactRank !== right.row.impactRank) return right.row.impactRank - left.row.impactRank;
                if (left.row.ageMs !== right.row.ageMs) return right.row.ageMs - left.row.ageMs;
                return left.index - right.index;
            })
            .map((entry) => entry.row);
    },

    /**
     * L350: a truncation notice needs a total to be honest about. The server states
     * the flag; the count is only quoted when the server also stated it.
     */
    truncationNotice: (dto: DailyQueuePageDto): string | null => {
        if (!dto.truncated) return null;
        if (dto.totalItems === null) {
            return "La fuente recortó esta cola. Acota el alcance o la ventana para ver el resto.";
        }
        return `Se muestran ${dto.rows.length} de ${dto.totalItems} operaciones que coinciden. Acota el alcance o la ventana para ver el resto.`;
    },

    /**
     * D99: a refused term belongs next to the box that caused it, in words the
     * operator can act on. Any other failure stays with the queue, untouched.
     */
    searchError: (error: DailyError | null): string | null => {
        if (error === null) return null;
        if (error.message !== INVALID_SEARCH_TERM) return null;
        return INVALID_SEARCH_COPY;
    },

    emptyMessage: (): string =>
        "No hay operaciones que coincidan con esta consulta. Esto no dice nada sobre la salud de la plataforma.",

};
